import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Button, Alert,
  FormControl, InputLabel, Select, MenuItem,
  TextField, Stack, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions,
  List, ListItem, ListItemText, IconButton,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import HelpOutlineIcon    from '@mui/icons-material/HelpOutline';
import CameraAltIcon      from '@mui/icons-material/CameraAlt';
import PhotoLibraryIcon   from '@mui/icons-material/PhotoLibrary';
import ReceiptLongIcon    from '@mui/icons-material/ReceiptLong';
import { uploadReceipt, uploadMissingReceipt, confirmReceipt, getStores } from '../api';
import AILoadingAnimation from './AILoadingAnimation';
import heic2any from 'heic2any';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const CATEGORIES = [
  { value: 'spelmateriaal',     label: 'Spelmateriaal',     emoji: '🎮', desc: 'Items voor het spelen van spellen zoals ballonnen, ballen, pionnen, sportelementen, speelgoed en knutselmateriaal.' },
  { value: 'Fourage',           label: 'Fourage',           emoji: '🍪', desc: 'Eten en drinken zoals koekjes, ranja, snacks, dranken en boodschappen.' },
  { value: 'Onderhoud vletten', label: 'Onderhoud vletten', emoji: '⛵', desc: 'Onderhoudsitems voor kleine zeilboten zoals verf, schuurpapier, lijnolie en bootbenodigdheden.' },
  { value: 'Onderhoud anders',  label: 'Onderhoud anders',  emoji: '🔧', desc: 'Andere onderhoudsitems zoals gereedschap, schoonmaakmiddelen en hardware (niet voor boten).' },
  { value: 'Diversen',          label: 'Diversen',          emoji: '📦', desc: 'Alles wat niet in de bovenstaande categorieën past.' },
];

// Categories for event (Hike/Kamp) receipts — these match the "Overzicht
// Kostenposten" labels in the event Excel template so totals line up.
const EVENT_CATEGORIES = [
  { value: 'Eten & drinken',      label: 'Eten & drinken',      emoji: '🍽️', desc: 'Boodschappen, maaltijden, snacks en dranken tijdens het kamp of de hike.' },
  { value: 'Programma / Spel',    label: 'Programma / Spel',    emoji: '🎯', desc: 'Materialen en kosten voor het programma, spellen en activiteiten.' },
  { value: 'Lokatie',             label: 'Lokatie',             emoji: '📍', desc: 'Kosten voor de kamplocatie, terrein of accommodatie.' },
  { value: 'Brandstof / Vervoer', label: 'Brandstof / Vervoer', emoji: '⛽', desc: 'Brandstof, openbaar vervoer en andere reiskosten.' },
  { value: 'Algemeen',            label: 'Algemeen',            emoji: '📦', desc: 'Overige kampkosten die niet in de andere categorieën passen.' },
];

function UploadReceipt({ members, activeGroup, onReceiptSaved }) {
  // Normal receipt state
  const [preview, setPreview] = useState(null);

  // Missing receipt state
  const [missingMode, setMissingMode]                           = useState(false);
  const [missingWarningAcknowledged, setMissingWarningAcknowledged] = useState(false);
  const [itemsFile, setItemsFile]       = useState(null);
  const [itemsPreview, setItemsPreview] = useState(null);
  const [bankFile, setBankFile]         = useState(null);
  const [bankPreview, setBankPreview]   = useState(null);

  // Shared state
  const [uploading, setUploading]               = useState(false);
  const [saving, setSaving]                     = useState(false);
  const [ocrResult, setOcrResult]               = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [uploadType, setUploadType]             = useState('regular');
  const [events, setEvents]                     = useState([]);
  const [formData, setFormData] = useState({
    member_id: '',
    amount:    '',
    category:  'Fourage',
    store:     '',
    remarks:   '',
    event_id:  '',
    date:      new Date().toISOString().split('T')[0],
  });
  const [message, setMessage]               = useState(null);
  const [dragging, setDragging]             = useState(false);
  const [draggingBank, setDraggingBank]     = useState(false);
  const [showCategoryHelp, setShowCategoryHelp] = useState(false);
  const [stores, setStores]                 = useState([]);
  const [customStore, setCustomStore]       = useState('');

  // Category set depends on the upload type: events use the camp categories.
  const activeCategories = uploadType === 'event' ? EVENT_CATEGORIES : CATEGORIES;

  useEffect(() => {
    const loadStores = async () => {
      try {
        const response = await getStores(activeGroup);
        setStores(response.stores || []);
      } catch (err) { console.error('Failed to load stores:', err); }
    };
    const loadEventsLocal = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/events`, { params: { group_name: activeGroup } });
        setEvents(response.data.filter(e => e.status === 'active'));
      } catch (err) { console.error('Failed to load events:', err); }
    };
    loadStores();
    loadEventsLocal();
  }, [activeGroup]); // eslint-disable-line react-hooks/exhaustive-deps

  const compressImage = async (file) => {
    let workingFile = file;

    // iPhones save photos as HEIC by default, which browsers can't decode in an
    // <img>. Convert to JPEG first so the canvas step (and preview) work.
    const isHeic = /image\/hei[cf]/i.test(file.type) || /\.hei[cf]$/i.test(file.name || '');
    if (isHeic) {
      try {
        const converted = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.95 });
        const blob = Array.isArray(converted) ? converted[0] : converted;
        workingFile = new File([blob], (file.name || 'foto').replace(/\.hei[cf]$/i, '.jpg'), { type: 'image/jpeg' });
      } catch (e) {
        throw new Error('Kon de HEIC-foto niet verwerken. Probeer een JPG/PNG, of zet je iPhone op Instellingen → Camera → Formaten → "Meest compatibel".');
      }
    }

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(new Error('Kon de afbeelding niet lezen.'));
      reader.onload = (e) => {
        const img = new Image();
        // Without this, an undecodable image would hang forever (onload never fires).
        img.onerror = () => reject(new Error('Dit afbeeldingsformaat wordt niet ondersteund. Gebruik een JPG of PNG.'));
        img.onload = () => {
          try {
            const canvas = document.createElement('canvas');
            let { width, height } = img;
            const maxSize = 2560;
            if (width > maxSize || height > maxSize) {
              if (width > height) { height = (height / width) * maxSize; width = maxSize; }
              else                { width = (width / height) * maxSize;  height = maxSize; }
            }
            canvas.width = width; canvas.height = height;
            canvas.getContext('2d').drawImage(img, 0, 0, width, height);
            canvas.toBlob(
              blob => resolve(blob ? new File([blob], workingFile.name, { type: 'image/jpeg' }) : workingFile),
              'image/jpeg', 0.95,
            );
          } catch (err) { reject(err); }
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(workingFile);
    });
  };

  const makePreview = (file, setter) => {
    const reader = new FileReader();
    reader.onloadend = () => setter(reader.result);
    reader.readAsDataURL(file);
  };

  // ── Normal receipt flow ──────────────────────────────────────────────────
  const processNormalFile = async (file) => {
    if (!file) return;
    setMessage(null);
    setUploading(true);
    try {
      const compressed = await compressImage(file);
      makePreview(compressed, setPreview);
      const result = await uploadReceipt(compressed);
      setOcrResult(result);
      setMessage(result.ocr_failed
        ? { type: 'warning', text: '⚠️ AI herkenning mislukt! Vul alle gegevens handmatig in op basis van het bonnetje.' }
        : null);
      prefillForm(result);
      setShowConfirmation(true);
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Fout bij uploaden' });
    } finally {
      setUploading(false);
    }
  };

  // ── Missing receipt flow ─────────────────────────────────────────────────
  const handleItemsFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setMessage(null); setUploading(true);
    try {
      const compressed = await compressImage(file);
      setItemsFile(compressed); makePreview(compressed, setItemsPreview);
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Kon de afbeelding niet verwerken' });
    } finally { setUploading(false); }
  };
  const handleBankFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setMessage(null); setUploading(true);
    try {
      const compressed = await compressImage(file);
      setBankFile(compressed); makePreview(compressed, setBankPreview);
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Kon de afbeelding niet verwerken' });
    } finally { setUploading(false); }
  };

  const handleMissingUpload = async () => {
    if (!itemsFile || !bankFile) { setMessage({ type: 'error', text: 'Upload beide afbeeldingen om door te gaan.' }); return; }
    setMessage(null); setUploading(true);
    try {
      const result = await uploadMissingReceipt(itemsFile, bankFile);
      setOcrResult(result);
      setMessage(result.ocr_failed ? { type: 'warning', text: '⚠️ AI herkenning mislukt! Vul alle gegevens handmatig in.' } : null);
      prefillForm(result);
      setShowConfirmation(true);
    } catch (err) { setMessage({ type: 'error', text: 'Fout bij uploaden: ' + err.message }); }
    setUploading(false);
  };

  // ── Shared helpers ───────────────────────────────────────────────────────
  const prefillForm = (result) => {
    let extractedDate = result.extracted_date;
    if (extractedDate?.includes('-')) {
      const parts = extractedDate.split('-');
      if (parts.length === 3 && parts[0].length <= 2)
        extractedDate = `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
    }
    // Only accept the AI-extracted category if it's valid for the current
    // upload type (the OCR uses the regular taxonomy, not the camp one).
    const extractedCategoryValid =
      result.extracted_category && activeCategories.some(c => c.value === result.extracted_category);
    setFormData(prev => ({
      ...prev,
      amount:    result.extracted_amount || '',
      member_id: members.find(m => m.name === result.extracted_member)?.id || '',
      date:      extractedDate || prev.date,
      category:  extractedCategoryValid ? result.extracted_category : prev.category,
      store:     result.extracted_store || '',
    }));
  };

  const handleConfirm = async () => {
    if (!formData.member_id || !formData.amount || !formData.category || !formData.date) {
      setMessage({ type: 'error', text: 'Vul alle velden in' }); return;
    }
    setSaving(true);
    try {
      await confirmReceipt({
        temp_path:                ocrResult.temp_path,
        member_id:                formData.member_id,
        amount:                   parseFloat(formData.amount),
        category:                 formData.category,
        store:                    formData.store,
        remarks:                  formData.remarks,
        event_id:                 uploadType === 'event' ? formData.event_id : null,
        date:                     formData.date,
        bank_statement_temp_path: ocrResult.bank_statement_temp_path || null,
        receipt_type:             ocrResult.receipt_type || 'normal',
        group_name:               activeGroup,
      });
      setMessage({ type: 'success', text: 'Bonnetje succesvol opgeslagen!' });
      try { const r = await getStores(activeGroup); setStores(r.stores || []); } catch (_) {}
      resetAll();
      if (onReceiptSaved) onReceiptSaved();
    } catch (err) { setMessage({ type: 'error', text: 'Fout bij opslaan: ' + err.message }); }
    setSaving(false);
  };

  const resetAll = () => {
    setPreview(null);
    setItemsFile(null); setItemsPreview(null);
    setBankFile(null); setBankPreview(null);
    setOcrResult(null); setShowConfirmation(false);
    setSaving(false);
    setCustomStore(''); setUploadType('regular');
    setMissingMode(false); setMissingWarningAcknowledged(false);
    setFormData({ member_id: '', amount: '', category: 'Fourage', store: '', remarks: '', event_id: '', date: new Date().toISOString().split('T')[0] });
  };

  // ── Drag helpers ─────────────────────────────────────────────────────────
  const handleDrop = (e) => {
    e.preventDefault(); setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file?.type.startsWith('image/')) processNormalFile(file);
  };
  const handleItemsDrop = async (e) => {
    e.preventDefault(); setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file?.type.startsWith('image/')) { const c = await compressImage(file); setItemsFile(c); makePreview(c, setItemsPreview); }
  };
  const handleBankDrop = async (e) => {
    e.preventDefault(); setDraggingBank(false);
    const file = e.dataTransfer.files[0];
    if (file?.type.startsWith('image/')) { const c = await compressImage(file); setBankFile(c); makePreview(c, setBankPreview); }
  };

  const uploadAreaSx = (isDragging) => (theme) => ({
    border: '2px dashed',
    borderColor: isDragging ? 'primary.dark' : 'primary.main',
    borderRadius: 2,
    p: { xs: 3, sm: 5 },
    textAlign: 'center',
    bgcolor: isDragging ? alpha(theme.palette.primary.main, 0.08) : 'background.default',
    cursor: 'pointer',
    transition: 'all 0.2s',
    '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05) },
  });

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <Paper elevation={1} sx={{ borderRadius: 2, p: { xs: 2, sm: 3 } }}>
      {uploading && (
        <AILoadingAnimation
          activeGroup={activeGroup}
          message={missingMode ? 'AI analyseert bewijs...' : 'AI analyseert bonnetje...'}
        />
      )}

      <Typography variant="h5" fontWeight={700} mb={2}>Bonnetje Uploaden</Typography>

      {message && (
        <Alert severity={message.type === 'error' ? 'error' : message.type === 'warning' ? 'warning' : 'success'} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      {/* ── Confirmation form ── */}
      {showConfirmation ? (
        <Box>
          {/* Image previews */}
          {missingMode ? (
            <Stack direction="row" spacing={1.5} mb={2} flexWrap="wrap">
              {itemsPreview && (
                <Box flex={1} minWidth={140}>
                  <Typography variant="caption" fontWeight={600} color="text.secondary" display="block" mb={0.5}>Lijst gekochte items</Typography>
                  <Box component="img" src={itemsPreview} alt="Items" sx={{ maxWidth: '100%', borderRadius: 1 }} />
                </Box>
              )}
              {bankPreview && (
                <Box flex={1} minWidth={140}>
                  <Typography variant="caption" fontWeight={600} color="text.secondary" display="block" mb={0.5}>Bankafschrift</Typography>
                  <Box component="img" src={bankPreview} alt="Bankafschrift" sx={{ maxWidth: '100%', borderRadius: 1 }} />
                </Box>
              )}
            </Stack>
          ) : preview && (
            <Box component="img" src={preview} alt="Preview" sx={{ maxWidth: '100%', borderRadius: 1, mb: 2, display: 'block' }} />
          )}

          {ocrResult && !ocrResult.ocr_failed && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <strong>AI Herkenning:</strong><br />
              Bedrag: {ocrResult.extracted_amount ? `€${ocrResult.extracted_amount}` : 'Niet herkend'}<br />
              Naam: {ocrResult.extracted_member || 'Niet herkend'}<br />
              <Typography variant="caption" color="text.secondary">Controleer en pas aan indien nodig</Typography>
              {ocrResult.raw_text && (
                <details style={{ marginTop: 8 }}>
                  <summary style={{ cursor: 'pointer', fontSize: 12, color: '#666' }}>Toon herkende tekst</summary>
                  <Box component="pre" sx={{ fontSize: 11, bgcolor: 'grey.100', p: 1, borderRadius: 1, maxHeight: 150, overflow: 'auto', mt: 0.5, whiteSpace: 'pre-wrap' }}>
                    {ocrResult.raw_text}
                  </Box>
                </details>
              )}
            </Alert>
          )}

          <Stack spacing={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Type Bonnetje *</InputLabel>
              <Select value={uploadType} label="Type Bonnetje *"
                onChange={e => {
                  const next = e.target.value;
                  setUploadType(next);
                  const defaultCat = next === 'event' ? EVENT_CATEGORIES[0].value : 'Fourage';
                  setFormData(f => ({
                    ...f,
                    category: defaultCat,
                    ...(next === 'regular' ? { event_id: '' } : {}),
                  }));
                }}>
                <MenuItem value="regular">🏠 Reguliere Opkomst</MenuItem>
                <MenuItem value="event">🎯 Evenement (Hike/Kamp)</MenuItem>
              </Select>
            </FormControl>

            {uploadType === 'event' && (
              <FormControl fullWidth size="small">
                <InputLabel>Selecteer Evenement *</InputLabel>
                <Select value={formData.event_id} label="Selecteer Evenement *" displayEmpty
                  onChange={e => setFormData(f => ({ ...f, event_id: e.target.value }))}>
                  <MenuItem value=""><em>-- Kies een evenement --</em></MenuItem>
                  {events.map(ev => (
                    <MenuItem key={ev.id} value={ev.id}>{ev.title} (Saldo: €{ev.balance.toFixed(2)})</MenuItem>
                  ))}
                </Select>
                {events.length === 0 && <Typography variant="caption" color="error">Geen actieve evenementen. Maak eerst een evenement aan.</Typography>}
              </FormControl>
            )}

            <FormControl fullWidth size="small">
              <InputLabel>Staflid *</InputLabel>
              <Select value={formData.member_id} label="Staflid *" displayEmpty
                onChange={e => setFormData(f => ({ ...f, member_id: e.target.value }))}>
                <MenuItem value=""><em>Selecteer een staflid</em></MenuItem>
                {members
                  .filter(m => uploadType === 'event' || m.name !== 'Voorschot')
                  .map(m => <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>)}
              </Select>
            </FormControl>

            <TextField fullWidth size="small" label="Bedrag (€) *" type="number"
              inputProps={{ step: '0.01' }} placeholder="12.50"
              value={formData.amount} onChange={e => setFormData(f => ({ ...f, amount: e.target.value }))} />

            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <Typography variant="body2" fontWeight={500}>Categorie *</Typography>
                <IconButton size="small" onClick={() => setShowCategoryHelp(true)} title="Uitleg categorieën" sx={{ p: 0.5 }}>
                  <HelpOutlineIcon fontSize="small" />
                </IconButton>
              </Box>
              <FormControl fullWidth size="small">
                <Select value={activeCategories.some(c => c.value === formData.category) ? formData.category : ''}
                  displayEmpty
                  onChange={e => setFormData(f => ({ ...f, category: e.target.value }))}>
                  {!activeCategories.some(c => c.value === formData.category) && (
                    <MenuItem value="" disabled><em>-- Kies een categorie --</em></MenuItem>
                  )}
                  {activeCategories.map(c => <MenuItem key={c.value} value={c.value}>{c.emoji} {c.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Box>

            <Box>
              <FormControl fullWidth size="small">
                <InputLabel>Winkel</InputLabel>
                <Select
                  value={customStore ? '__custom__' : formData.store}
                  label="Winkel"
                  displayEmpty
                  onChange={e => {
                    if (e.target.value === '__custom__') { setFormData(f => ({ ...f, store: '' })); setCustomStore('__editing__'); }
                    else { setFormData(f => ({ ...f, store: e.target.value })); setCustomStore(''); }
                  }}
                >
                  <MenuItem value=""><em>-- Kies een winkel of vul handmatig in --</em></MenuItem>
                  {stores.map((s, i) => <MenuItem key={i} value={s}>{s}</MenuItem>)}
                  <MenuItem value="__custom__">✏️ Andere winkel...</MenuItem>
                </Select>
              </FormControl>
              {customStore && (
                <TextField fullWidth size="small" placeholder="Voer winkelnaam in" autoFocus sx={{ mt: 1 }}
                  value={formData.store} onChange={e => setFormData(f => ({ ...f, store: e.target.value }))} />
              )}
            </Box>

            <TextField fullWidth size="small" label="Opmerkingen / Speciaal Programma" placeholder="Optioneel"
              value={formData.remarks} onChange={e => setFormData(f => ({ ...f, remarks: e.target.value }))} />

            <TextField fullWidth size="small" label="Datum *" type="date"
              InputLabelProps={{ shrink: true }}
              value={formData.date} onChange={e => setFormData(f => ({ ...f, date: e.target.value }))} />
          </Stack>

          <Stack direction="row" spacing={1.5} justifyContent="flex-end" mt={3}>
            <Button variant="outlined" color="inherit" onClick={resetAll} disabled={saving}>Annuleren</Button>
            <Button variant="contained" color="success" onClick={handleConfirm} disabled={saving}>
              {saving ? 'Opslaan...' : 'Opslaan'}
            </Button>
          </Stack>
        </Box>

      /* ── Missing receipt: warning screen ── */
      ) : missingMode && !missingWarningAcknowledged ? (
        <Box>
          <Alert severity="warning" icon={false} sx={{ mb: 2, border: 2, borderColor: 'warning.main' }}>
            <Typography variant="subtitle1" fontWeight={700} color="warning.dark" mb={1}>⚠️ Let op: laatste redmiddel</Typography>
            <Typography variant="body2" color="warning.dark" mb={1}>
              <strong>Gebruik dit formulier alleen als je het originele bonnetje echt niet meer hebt.</strong>
            </Typography>
            <Box component="ul" sx={{ color: 'warning.dark', pl: 2.5, m: 0 }}>
              <Typography component="li" variant="body2" mb={0.5}>Probeer altijd eerst het originele bonnetje te vinden of een kopie te downloaden van de winkel app.</Typography>
              <Typography component="li" variant="body2" mb={0.5}>Een bonnetje is het enige officiële bewijs van aankoop — dit alternatief is slechts een uitzondering.</Typography>
              <Typography component="li" variant="body2">
                Je hebt twee afbeeldingen nodig:
                <Box component="ol" sx={{ pl: 2.5, mt: 0.5 }}>
                  <Typography component="li" variant="body2">Een foto of lijst van de gekochte items</Typography>
                  <Typography component="li" variant="body2">Een foto van het bankafschrift waarop de afschrijving te zien is</Typography>
                </Box>
              </Typography>
            </Box>
          </Alert>
          <Stack direction="row" spacing={1.5} justifyContent="flex-end">
            <Button variant="outlined" color="inherit" onClick={() => { setMissingMode(false); setMessage(null); }}>
              Terug naar normaal bonnetje
            </Button>
            <Button variant="contained" color="warning" onClick={() => setMissingWarningAcknowledged(true)}>
              Ik begrijp het, ga verder
            </Button>
          </Stack>
        </Box>

      /* ── Missing receipt: dual image upload ── */
      ) : missingMode && missingWarningAcknowledged ? (
        <Box>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <strong>Alternatief bewijs</strong> — upload een foto van je itemslijst én een foto van het bankafschrift.
          </Alert>

          <Typography variant="subtitle2" fontWeight={600} mb={1}>1. Foto van lijst met gekochte items</Typography>
          {itemsPreview ? (
            <Box mb={2}>
              <Box component="img" src={itemsPreview} alt="Items preview" sx={{ maxWidth: '100%', borderRadius: 1, mb: 1, display: 'block' }} />
              <Button size="small" variant="outlined" onClick={() => { setItemsFile(null); setItemsPreview(null); }}>Andere foto kiezen</Button>
            </Box>
          ) : (
            <Box sx={uploadAreaSx(dragging)} mb={2.5}
              onDrop={handleItemsDrop}
              onDragOver={e => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
            >
              <Typography fontSize="1.5rem" mb={0.5}>📝</Typography>
              <Typography fontWeight={600} mb={1.5}>Itemslijst foto</Typography>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                justifyContent="center"
                sx={{ width: '100%' }}
              >
                <Button variant="contained" startIcon={<CameraAltIcon />}
                  onClick={() => document.getElementById('items-camera-input').click()} disabled={uploading}>
                  Foto maken
                </Button>
                <Button variant="contained" startIcon={<PhotoLibraryIcon />} color="secondary"
                  onClick={() => document.getElementById('items-gallery-input').click()} disabled={uploading}>
                  Galerij
                </Button>
              </Stack>
              <input id="items-camera-input" type="file" accept="image/*" capture="environment" onChange={handleItemsFileSelect} disabled={uploading} style={{ display: 'none' }} />
              <input id="items-gallery-input" type="file" accept="image/*" onChange={handleItemsFileSelect} disabled={uploading} style={{ display: 'none' }} />
            </Box>
          )}

          <Typography variant="subtitle2" fontWeight={600} mb={1}>2. Foto van bankafschrift</Typography>
          {bankPreview ? (
            <Box mb={2}>
              <Box component="img" src={bankPreview} alt="Bankafschrift preview" sx={{ maxWidth: '100%', borderRadius: 1, mb: 1, display: 'block' }} />
              <Button size="small" variant="outlined" onClick={() => { setBankFile(null); setBankPreview(null); }}>Andere foto kiezen</Button>
            </Box>
          ) : (
            <Box sx={uploadAreaSx(draggingBank)} mb={2.5}
              onDrop={handleBankDrop}
              onDragOver={e => { e.preventDefault(); setDraggingBank(true); }}
              onDragLeave={() => setDraggingBank(false)}
            >
              <Typography fontSize="1.5rem" mb={0.5}>🏦</Typography>
              <Typography fontWeight={600} mb={1.5}>Bankafschrift foto</Typography>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                justifyContent="center"
                sx={{ width: '100%' }}
              >
                <Button variant="contained" startIcon={<CameraAltIcon />}
                  onClick={() => document.getElementById('bank-camera-input').click()} disabled={uploading}>
                  Foto maken
                </Button>
                <Button variant="contained" startIcon={<PhotoLibraryIcon />} color="secondary"
                  onClick={() => document.getElementById('bank-gallery-input').click()} disabled={uploading}>
                  Galerij
                </Button>
              </Stack>
              <input id="bank-camera-input" type="file" accept="image/*" capture="environment" onChange={handleBankFileSelect} disabled={uploading} style={{ display: 'none' }} />
              <input id="bank-gallery-input" type="file" accept="image/*" onChange={handleBankFileSelect} disabled={uploading} style={{ display: 'none' }} />
            </Box>
          )}

          <Stack direction="row" spacing={1.5} justifyContent="flex-end" mt={1}>
            <Button variant="outlined" color="inherit"
              onClick={() => { setMissingMode(false); setMissingWarningAcknowledged(false); setItemsFile(null); setItemsPreview(null); setBankFile(null); setBankPreview(null); setMessage(null); }}>
              Terug
            </Button>
            <Button variant="contained" color="success" onClick={handleMissingUpload} disabled={uploading || !itemsFile || !bankFile}>
              {uploading ? 'Bezig...' : 'Analyseren en doorgaan'}
            </Button>
          </Stack>
        </Box>

      /* ── Normal receipt upload ── */
      ) : (
        <>
          <Paper variant="outlined" sx={(theme) => ({
            p: 2, mb: 2, borderRadius: 2,
            bgcolor: alpha(theme.palette.primary.main, 0.06),
            borderColor: alpha(theme.palette.primary.main, 0.25),
          })}>
            <Typography variant="subtitle2" color="primary" fontWeight={700} mb={1}>📋 Instructies</Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {[
                'Maak een duidelijke foto van het hele bonnetje',
                'Zorg voor goede verlichting zonder schaduwen',
                'Schrijf je naam op het bonnetje, zodat de AI hem kan herkennen of vul deze na het uploaden handmatig in',
                'Als het totaal op het bonnetje niet klopt: streep het bedrag door en schrijf het correcte bedrag ernaast',
                'De AI herkent automatisch: bedrag, naam, datum, categorie en winkel',
              ].map((item, i) => (
                <Typography key={i} component="li" variant="body2" color="text.secondary" mb={0.25}>{item}</Typography>
              ))}
              <Typography component="li" variant="body2" color="warning.main" fontWeight={600}>Controleer altijd de ingevulde gegevens en vul ontbrekende velden aan!</Typography>
            </Box>
          </Paper>

          <Box sx={uploadAreaSx(dragging)}
            onDrop={handleDrop}
            onDragOver={e => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
          >
            <Typography fontSize="3rem" mb={0.5}>📸</Typography>
            <Typography variant="h6" fontWeight={600} mb={2}>Upload een bonnetje</Typography>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              justifyContent="center"
              sx={{ width: '100%' }}
            >
              <Button variant="contained" size="large" startIcon={<CameraAltIcon />}
                onClick={() => document.getElementById('camera-input').click()} disabled={uploading}>
                Foto maken
              </Button>
              <Button variant="contained" size="large" startIcon={<PhotoLibraryIcon />} color="secondary"
                onClick={() => document.getElementById('gallery-input').click()} disabled={uploading}>
                Galerij kiezen
              </Button>
            </Stack>
            <Typography variant="caption" color="text.disabled" display="block" mt={2}>
              of sleep een afbeelding hierheen
            </Typography>
            <input id="camera-input" type="file" accept="image/*" capture="environment" onChange={e => processNormalFile(e.target.files[0])} disabled={uploading} style={{ display: 'none' }} />
            <input id="gallery-input" type="file" accept="image/*" onChange={e => processNormalFile(e.target.files[0])} disabled={uploading} style={{ display: 'none' }} />
          </Box>

          <Box textAlign="center" mt={2}>
            <Button
              variant="outlined"
              size="small"
              color="warning"
              startIcon={<ReceiptLongIcon />}
              onClick={() => { setMissingMode(true); setMessage(null); }}
              sx={{ borderStyle: 'dashed' }}
            >
              Geen bonnetje meer? Gebruik alternatief bewijs
            </Button>
          </Box>
        </>
      )}

      {/* Category help dialog */}
      <Dialog open={showCategoryHelp} onClose={() => setShowCategoryHelp(false)} maxWidth="sm">
        <DialogTitle>📋 Categorie Uitleg</DialogTitle>
        <DialogContent>
          <List disablePadding>
            {activeCategories.map((cat, i) => (
              <React.Fragment key={cat.value}>
                {i > 0 && <Divider />}
                <ListItem sx={(theme) => ({ bgcolor: alpha(theme.palette.primary.main, 0.04), borderLeft: 4, borderColor: 'primary.main', mb: 0.5, borderRadius: '0 4px 4px 0' })}>
                  <ListItemText
                    primary={`${cat.emoji} ${cat.label}`}
                    secondary={cat.desc}
                    primaryTypographyProps={{ fontWeight: 700, color: 'primary.main' }}
                    secondaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setShowCategoryHelp(false)}>Sluiten</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

export default UploadReceipt;
